// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type ProgressNamespace = components["schemas"]["ProgressNamespaceEnum"];
export type TaskProgressReading = components["schemas"]["TaskProgressCache"];

/** How often a running task's counter is re-read while the request that drives it is in flight. */
const PROGRESS_POLL_INTERVAL = 1_000;

function progressQueryKey(namespace: ProgressNamespace, taskId: string | null) {
  return ["progress", namespace, taskId] as const;
}

interface TaskProgressParams {
  namespace: ProgressNamespace;
  /** Id the running task reports under — the import session id for assisted imports. */
  taskId: string | null;
  /** Poll only while the long-running request is actually in flight. */
  active: boolean;
  /**
   * Total the caller already knows, when it does. Readings that disagree are discarded as
   * leftovers of an earlier task that reported under the same id.
   */
  expectedTotal?: number;
  intervalMs?: number;
}

/**
 * Polls `GET /v1/progress` for the counter a long-running back-end task publishes into Redis.
 *
 * Progress is absent (`null`) until the task calls `start()`, and expires an hour after the last
 * update, so a missing reading is a normal state rather than an error.
 */
export function useTaskProgressQuery({
  namespace,
  taskId,
  active,
  intervalMs = PROGRESS_POLL_INTERVAL,
}: TaskProgressParams) {
  return useQuery({
    queryKey: progressQueryKey(namespace, taskId),
    enabled: active && !!taskId,
    refetchInterval: intervalMs,
    // A live counter is never worth serving from cache, and there is nothing to keep once the
    // task it belongs to is done.
    staleTime: 0,
    gcTime: 0,
    // One dropped poll must neither retry in a burst nor stop the loop — the next tick recovers.
    retry: false,
    queryFn: async () => {
      const res = await api.GET("/v1/progress", {
        params: { query: { namespace, taskId: taskId ?? "" } },
      });
      if (res.error || !res.data) throw new Error("Failed to read task progress");
      return (res.data as components["schemas"]["TaskProgressResponse"]).progress ?? null;
    },
  });
}

export interface RunningTaskProgress {
  current: number;
  total: number;
  /** Completed share of the work, 0..1. */
  fraction: number;
}

/**
 * Progress of the task identified by `namespace` + `taskId`, or `null` while nothing trustworthy
 * has been reported for the current run.
 *
 * The back end keys progress by task id alone, and the assisted-import wizard reuses a single
 * import-session id for both prepare and submit. A poll issued the moment submit starts can
 * therefore still read the *finished* counter prepare left behind (it lives for an hour), so two
 * guards apply before a reading is shown:
 *
 * - the cached reading of the previous run is dropped when a new run begins, and
 * - a reading is only trusted once a genuinely partial one has arrived, because a fresh reporter
 *   always publishes `current: 0` first — a full bar before that is the previous run's leftover.
 */
export function useTaskProgress(params: TaskProgressParams): RunningTaskProgress | null {
  const { namespace, taskId, active, expectedTotal } = params;
  const queryClient = useQueryClient();
  const { data: reading } = useTaskProgressQuery(params);
  const [trusted, setTrusted] = useState(false);

  // Readings for another total belong to an earlier task that reported under the same id.
  const accepted = reading && (expectedTotal == null || reading.total === expectedTotal) ? reading : null;

  useEffect(
    function restartTrackingWhenTaskStarts() {
      if (!active) return;
      setTrusted(false);
      queryClient.removeQueries({ queryKey: progressQueryKey(namespace, taskId) });
    },
    [active, namespace, taskId, queryClient],
  );

  useEffect(
    function trustReadingsOnceOneIsPartial() {
      if (!active || trusted || !accepted) return;
      if (accepted.current < accepted.total) setTrusted(true);
    },
    [active, trusted, accepted],
  );

  if (!active || !trusted || !accepted || accepted.total <= 0) return null;
  return {
    current: accepted.current,
    total: accepted.total,
    fraction: Math.min(1, accepted.current / accepted.total),
  };
}
